import { Directive, computed, effect, input, output, signal } from '@angular/core';
import { ApiSelectConfig, DwApiSelectObservable } from '@digiwall/core';
import { PlanningItemDto } from '@digiwall/goliath-documents/proxy';
import {
  FilterSettingsMap,
  PlanningBoardFilterState,
  includesText,
  matchesSelection,
} from '../../planning-common/filters';

interface FilterOption {
  id: string;
  label: string;
}

/** A dropdown control shown on the board: either a static option list or a lazy `dwApiSelect` lookup. */
interface DropdownFilter {
  key: string;
  mode: 'static' | 'api';
  /** Static mode: the resolved option list. */
  options?: FilterOption[];
  /** Api mode: the lazy lookup loader (constrained to preset ids internally when ≥2 presets). */
  apiSelect?: DwApiSelectObservable;
  bindLabel: string;
}

/** An api-backed dropdown descriptor a concrete filter registers per key (e.g. customer, article). */
export interface ApiDropdown {
  apiSelect: DwApiSelectObservable;
  bindLabel: string;
}

/** Stable empty reference for an unset selection (a fresh [] each CD would churn ng-select → freeze). */
const EMPTY_SELECTION: string[] = [];

/**
 * Shared logic for a data source's planner board filter. Reads the configured presets and renders
 * interactive controls: a dropdown per enabled multi-select filter with ≥2 preset values (options
 * constrained to those presets; a 1-value preset is hidden because the data is already narrowed
 * server-side, and a 0-preset filter lists every distinct value in the loaded items), and a text
 * input per enabled free-text filter. Emits a predicate over this source's items on every change so
 * the board narrows backlog + columns instantly, with no network call.
 *
 * A concrete filter supplies only the source-specific parts — the filter keys, their labels, how a
 * key maps to a value on an item's `displayFields`, and how an option id resolves to a display label.
 * A new data source subclasses this and reuses all of the rendering + state + predicate plumbing.
 */
@Directive()
export abstract class BaseBoardFilterComponent {
  readonly filterSettings = input<FilterSettingsMap>({});
  /** This source's currently loaded items — used to label options and derive unconstrained options. */
  readonly items = input<PlanningItemDto[]>([]);
  /** Restored filter selection to seed the controls with (from the board's persisted view state). */
  readonly state = input<PlanningBoardFilterState>();
  readonly filterChange = output<(dto: PlanningItemDto) => boolean>();
  /** Serializable snapshot of the current selection, emitted on every change for persistence. */
  readonly stateChange = output<PlanningBoardFilterState>();

  /** Dropdown (multi-select) filter keys — the only ones allowed to carry preset values. */
  protected abstract readonly dropdownKeys: readonly string[];
  /** Free-text filter keys — client-side substring only, never carry preset values. */
  protected abstract readonly textKeys: readonly string[];
  /** Localization key per filter key, shown as the control placeholder. */
  protected abstract readonly labelKey: Record<string, string>;

  /** Value of a filter field on an item's loosely-typed `displayFields` (for matching + options). */
  protected abstract fieldValue(key: string, displayFields: unknown): string | undefined;
  /** Human label for a dropdown option id (may read this source's loaded items / lookup signals). */
  protected abstract optionLabel(key: string, id: string): string;

  /**
   * Dropdown keys rendered as a lazy `dwApiSelect` lookup instead of a static option list — the loader
   * resolves labels natively and pages large lists, and (via the concrete impl) constrains results to
   * the configured preset ids. Keys absent here fall back to the static option list.
   */
  protected readonly apiDropdowns: Record<string, ApiDropdown> = {};

  /** Lazy config for the api combos: don't fetch on display, only when the dropdown is first opened. */
  protected readonly apiSelectConfig: Partial<ApiSelectConfig> = { loadOnOpen: true };

  private readonly selection = signal<Record<string, string[]>>({});
  private readonly text = signal<Record<string, string>>({});

  // No initial emit: the board treats a missing predicate as pass-all, so there is nothing to send
  // until the user interacts. Emitting during construction would write a board signal mid-CD (NG0103).

  constructor() {
    // Restore a persisted selection when the board supplies one: seed the controls, then apply the
    // predicate to the board. The predicate emit is deferred to a microtask so it doesn't write the
    // board's predicate signal during the current change-detection pass (same reason as the note above).
    effect(() => {
      const state = this.state();
      if (!state) {
        return;
      }
      this.selection.set(state.selection ?? {});
      this.text.set(state.text ?? {});
      queueMicrotask(() => this.emit());
    });
  }

  // ── Visible controls ─────────────────────────────────────────────────────────

  /**
   * Dropdown filters shown interactively, per enabled multi-select filter:
   * - ≥2 preset values → options constrained to those presets;
   * - exactly 1 preset → hidden (the data is already narrowed to it server-side, nothing to refine);
   * - 0 presets → options are every distinct value present in the loaded items (unconstrained).
   */
  protected readonly dropdownFilters = computed<DropdownFilter[]>(() => {
    const settings = this.filterSettings() ?? {};
    const result: DropdownFilter[] = [];
    for (const key of this.dropdownKeys) {
      const setting = settings[key];
      if (!setting?.enabled) {
        continue;
      }
      const presets = setting.values ?? [];
      if (presets.length === 1) {
        continue; // narrowed to a single value server-side — no interactive control needed
      }
      const api = this.apiDropdowns[key];
      if (api) {
        // The loader reads the presets itself to constrain results; the ref stays stable.
        result.push({ key, mode: 'api', apiSelect: api.apiSelect, bindLabel: api.bindLabel });
      } else {
        const values = presets.length >= 2 ? presets : this.distinctItemValues(key);
        result.push({
          key,
          mode: 'static',
          options: this.optionsFor(key, values),
          bindLabel: 'label',
        });
      }
    }
    return result;
  });

  /** Free-text filters shown as inputs: enabled. */
  protected readonly textFilters = computed<string[]>(() => {
    const settings = this.filterSettings() ?? {};
    return this.textKeys.filter(key => settings[key]?.enabled);
  });

  /** Distinct values for a key present in the currently loaded items (options for the 0-preset case). */
  private distinctItemValues(key: string): string[] {
    const seen = new Set<string>();
    for (const dto of this.items()) {
      const value = this.fieldValue(key, dto.displayFields ?? {});
      if (value) {
        seen.add(value);
      }
    }
    return [...seen];
  }

  private optionsFor(key: string, values: string[]): FilterOption[] {
    return values.map(id => ({ id, label: this.optionLabel(key, id) }));
  }

  /**
   * Constrains the lookup's search page to the configured preset ids (≥2 case). The directive pre-fills
   * `ids` with the selected values when resolving their labels — leave that request untouched.
   */
  protected applyPresetIds(key: string, dto: { ids?: string[] }): void {
    const presets = this.filterSettings()?.[key]?.values ?? [];
    if (presets.length >= 2 && (!dto.ids || dto.ids.length === 0)) {
      dto.ids = [...presets];
    }
  }

  // ── User input ───────────────────────────────────────────────────────────────

  protected selectionOf(key: string): string[] {
    return this.selection()[key] ?? EMPTY_SELECTION;
  }

  protected setSelection(key: string, values: string[]): void {
    this.selection.update(all => ({ ...all, [key]: values }));
    this.emit();
    this.emitState();
  }

  protected textOf(key: string): string {
    return this.text()[key] ?? '';
  }

  protected setText(key: string, value: string): void {
    this.text.update(all => ({ ...all, [key]: value }));
    this.emit();
    this.emitState();
  }

  /** Emits the serializable selection so the board can persist this source's active filters. */
  private emitState(): void {
    this.stateChange.emit({ selection: this.selection(), text: this.text() });
  }

  private emit(): void {
    const selection = this.selection();
    const text = this.text();
    this.filterChange.emit((dto: PlanningItemDto) => {
      const fields = dto.displayFields ?? {};
      return (
        this.dropdownKeys.every(key =>
          matchesSelection(selection[key] ?? [], this.fieldValue(key, fields)),
        ) && this.textKeys.every(key => includesText(this.fieldValue(key, fields), text[key] ?? ''))
      );
    });
  }
}
